import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  Tooltip,
  Menu,
  ScrollArea,
  UnstyledButton,
  Avatar,
  Divider,
  Box,
  Button,
  Flex,
  Badge,
  Stack,
  Loader,
} from "@mantine/core";
import {
  IconLayoutGrid,
  IconHome,
  IconSearch,
  IconBriefcase,
  IconBookmark,
  IconHelpCircle,
  IconLogout,
  IconAlertTriangle,
  IconChevronRight,
  IconCoins,
  IconSettings,
  IconPencil,
  IconUser,
  IconBuildingPlus,
  IconLink,
  IconSend,
  IconUsersGroup,
  IconBell,
  IconNetwork,
  IconCalendarEvent,
  IconMail,
  IconCircleCheck,
  // IconUsers,
  // IconUserStar,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { AppShellRouteFallback } from "@/routes/RouteFallback";
import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useUserContext } from "@/context/UserContext";
import { useNotifications } from "@/context/NotificationContext";
import { COMPANY_HOME_PATH } from "@/pages/dashboard/dashboardTab.const";
import { CompleteProfilePrompt } from "@/components/onboarding/CompleteProfilePrompt";
import {
  getClusterSignupHomePath,
  isClusterSignupHomePath,
  MY_CLUSTER_PATH,
  notifyMyClusterCountChanged,
} from "@/pages/my-cluster/myCluster.const";
import { useMyEcosystemHeadCount } from "@/pages/dashboard/hooks/useMyEcosystemHeadCount";
import { useExhibitorInquiryUnseenCount } from "@/pages/my-expos/useExhibitorInquiryUnseenCount";
import { EXHIBITOR_INQUIRIES_PATH } from "@/pages/my-expos/exhibitorInquiries.const";
import { FLEX_DEMO_PATH, isFlexDemoViewportSupported } from "@/flex-demo/flexDemo.const";
import {
  EXPO_VISITOR_HOME_PATH,
  isClusterSignupPlatformIntentEnabled,
  isClusterSignupUser,
  isExpoSignupUser,
  isFlexDemoAssigned,
  isWebsiteProfileJobInProgress,
  shouldDeferCompanyProfile,
  shouldPromptCompanyRegistryVerification,
  WEBSITE_PROFILE_JOB_IN_PROGRESS_STATUSES,
} from "@/shared/constants/clientOnboarding.const";
import { useFlexDemoSessionOptional } from "@/flex-demo/FlexDemoSessionContext";
import {
  flexDemoNavLockedTooltip,
  isFlexDemoExploreStep,
  isFlexDemoNavAllowed,
  isFlexDemoWorkspaceStep,
} from "@/flex-demo/flexDemo.coach";
import { SidebarOnboardingTour } from "@/components/layout/SidebarOnboardingTour";
import { SidebarDesktopFooter } from "@/components/layout/SidebarDesktopFooter";
import { NewRequestsSpotlight } from "@/components/layout/NewRequestsSpotlight";
import ApplyVerificationModal from "@/pages/profile-verification/ApplyVerificationModal";
import ProfileWelcomeCreditsModal from "@/pages/profile-verification/ProfileWelcomeCreditsModal";
import CompanyIntentQuestionnaireModal from "@/pages/company-intent/CompanyIntentQuestionnaireModal";
import IntentQuestionnaireCompleteModal from "@/pages/company-intent/IntentQuestionnaireCompleteModal";
import EcosystemHeadCompanyQuestionnaireModal from "@/components/ecosystem-head/EcosystemHeadCompanyQuestionnaireModal";
import TrustpilotRatingModal from "@/components/shared/TrustpilotRatingModal";
import { useTrustpilotRatingPrompt } from "@/hooks/useTrustpilotRatingPrompt";
import {
  readPendingTrustpilotPrompt,
  syncTrustpilotPromptsFromServer,
  TRUSTPILOT_PROMPT_QUEUED_EVENT,
} from "@/shared/utils/trustpilotPrompt.util";
import { INTENT_QUESTIONNAIRE_SKIP_KEY, clearIntentQuestionnaireSkip, markIntentQuestionnaireSkippedInSession } from "@/shared/utils/companyIntentGate.util";
import AuthExploreTopHeader, {
  AUTH_MOBILE_EXPLORE_ROW_HEIGHT,
  AuthExploreMobileTabsRow,
} from "@/components/layout/AuthExploreTopHeader";
import { ExpoVisitorHeaderCopy } from "@/components/layout/ExpoVisitorHeaderCopy";
import { getExpoVisitorRouteHeader } from "@/components/layout/expoVisitorHeader.routes";
import { PROFILE_ONBOARDING_SKIP_CREDITS_KEY, PROFILE_WELCOME_CREDITS_KEY, verificationAutoOpenedSessionKey } from "@/shared/constants/onboarding.const";
import {
  COLLABORATION_WALLET_PATH,
  isCollaborationWalletPath,
} from "@/shared/constants/collaborationWallet.const";
import {
  clearGuestAuthIntent,
  readGuestAuthPostOnboardingPath,
} from "@/shared/utils/guestAuthIntent.util";
import trpc from "@/shared/utils/trpc";
import { errorNotification } from "@/shared/utils/errorNotification";
import {
  useApplyCompanyVerification,
} from "@/hooks/useApplyCompanyVerification";
import { SidebarRailToggleButton } from "@/components/layout/SidebarCollapseControls";
import { SidebarIconRailLink } from "@/components/layout/SidebarIconRail";
import TabCountBadge from "@/shared/components/TabCountBadge";
import { usePersistedSidebarOpen } from "@/components/layout/usePersistedSidebarOpen";
import { AppShellSidebarProvider } from "@/components/layout/AppShellSidebarContext";
import { useResponsiveSidebarLayout } from "@/shared/hooks/useResponsiveSidebarLayout";
import { PortalAppShellMain, PortalMainShell } from "@/components/layout/PortalMainShell";
import { SidebarLogoLink } from "@/components/layout/SidebarLogoLink";

const APP_SIDEBAR_STORAGE_KEY = "gt:sidebar-desktop-open:app";

/** Post-profile-create sequencer: verification → credits → questionnaire → tour. */
type OnboardingPhase =
  | "idle"
  | "credits"
  | "verification"
  | "tour"
  | "questionnaire"
  | "done";

type AppLayoutNavContextValue = {
  mobileOpened: boolean;
  toggleMobile: () => void;
  closeMobile: () => void;
};

const AppLayoutNavContext = createContext<AppLayoutNavContextValue | null>(null);

export function useAppLayoutNav() {
  const ctx = useContext(AppLayoutNavContext);
  if (!ctx) throw new Error("useAppLayoutNav must be used within <AppLayout />");
  return ctx;
}

type VerificationModalContextValue = {
  openVerificationModal: () => void;
};

const VerificationModalContext = createContext<VerificationModalContextValue | null>(null);

export function useVerificationModal() {
  const ctx = useContext(VerificationModalContext);
  if (!ctx) throw new Error("useVerificationModal must be used within <AppLayout />");
  return ctx;
}

const noopOpenVerificationModal = () => { };

/** Like useVerificationModal but safe on public layouts (e.g. Discover on `/` or `/discover`). */
export function useVerificationModalOpener(): () => void {
  const ctx = useContext(VerificationModalContext);
  return ctx?.openVerificationModal ?? noopOpenVerificationModal;
}

type CompanyIntentModalContextValue = {
  openCompanyIntentModal: () => void;
};

const CompanyIntentModalContext = createContext<CompanyIntentModalContextValue | null>(null);

const noopOpenCompanyIntentModal = () => { };

/** Opens the company intent questionnaire (clears any temporary skip). */
export function useCompanyIntentModalOpener(): () => void {
  const ctx = useContext(CompanyIntentModalContext);
  return ctx?.openCompanyIntentModal ?? noopOpenCompanyIntentModal;
}

interface ProfileContextType {
  isProfileCreated: boolean;
  setIsProfileCreated: (value: boolean) => void;
}

const ProfileContext = createContext<ProfileContextType>({
  isProfileCreated: false,
  setIsProfileCreated: () => { },
});

export const useProfileStatus = () => useContext(ProfileContext);
const PROFILE_VERIFICATION_LANDING_KEY = "gt:profileVerificationSummary";

// Company Status Banner Component
interface CompanyStatusBannerProps {
  adminBlocked: boolean;
  blockedReason?: string | null;
  companyStatus?: string;
  onViewMore: () => void;
}

function CompanyStatusBanner({
  adminBlocked,
  blockedReason,
  companyStatus,
  onViewMore,
  sidebarOffsetPx,
}: CompanyStatusBannerProps & { sidebarOffsetPx: number }) {
  if (adminBlocked) {
    return (
      <Box
        pos="fixed"
        bottom={0}
        left={{ base: 0, sm: sidebarOffsetPx }}
        right={0}
        py={16}
        px={32}
        style={{
          backgroundColor: "#FEE2E2",
          zIndex: 100,
          borderTop: "2px solid #FECACA",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconAlertTriangle size={24} color="#991B1B" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} c="#991B1B">
                Your account has been temporarily blocked please contact support.
              </Text>
              <Text size="sm" c="#B91C1C" truncate>
                {blockedReason ||
                  "Your profile has been disabled. You will not be able to perform certain actions like shortlist, exploring opportunities, etc."}{" "}
              </Text>
            </Box>
          </Group>
          <Button
            variant="filled"
            color="#D84B4B"
            size="sm"
            rightSection={<IconChevronRight size={16} />}
            onClick={onViewMore}
            style={{ flexShrink: 0 }}
          >
            View more
          </Button>
        </Group>
      </Box>
    );
  }

  if (companyStatus === "ACTION_REQUIRED") {
    return (
      <Box
        pos="fixed"
        bottom={0}
        left={{ base: 0, sm: sidebarOffsetPx }}
        right={0}
        py={16}
        px={32}
        style={{
          backgroundColor: "#FEE2E2",
          zIndex: 100,
          borderTop: "2px solid #FECACA",
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <IconAlertTriangle size={24} color="#991B1B" style={{ flexShrink: 0 }} />
            <Box style={{ minWidth: 0 }}>
              <Text size="md" fw={600} c="#991B1B">
                Your company could not be verified
              </Text>
              <Text size="sm" c="#B91C1C" truncate>
                Click to know more
              </Text>
            </Box>
          </Group>
          <Button
            variant="filled"
            color="#D84B4B"
            size="sm"
            rightSection={<IconChevronRight size={16} />}
            onClick={onViewMore}
            style={{ flexShrink: 0 }}
          >
            Go to Profile
          </Button>
        </Group>
      </Box>
    );
  }

  return null;
}

function CompleteProfileBanner({
  onComplete,
  sidebarOffsetPx,
}: {
  onComplete: () => void;
  sidebarOffsetPx: number;
}) {
  return (
    <Box
      pos="fixed"
      bottom={0}
      left={{ base: 0, sm: sidebarOffsetPx }}
      right={0}
      py={16}
      px={{ base: 16, sm: 32 }}
      style={{
        backgroundColor: "#FEF3C7",
        zIndex: 100,
        borderTop: "2px solid #FCD34D",
      }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Group gap="md" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
          <IconAlertTriangle size={24} color="#92400E" style={{ flexShrink: 0 }} />
          <Box style={{ minWidth: 0 }}>
            <Text size="md" fw={600} c="#92400E">
              Complete your company profile
            </Text>
            <Text size="sm" c="#B45309">
              Browse Explore and Home now. Finish your profile before you reach out to another company.
            </Text>
          </Box>
        </Group>
        <Button
          variant="filled"
          color="yellow"
          c="#04305c"
          size="sm"
          rightSection={<IconChevronRight size={16} />}
          onClick={onComplete}
          style={{ flexShrink: 0 }}
        >
          Complete profile
        </Button>
      </Group>
    </Box>
  );
}

const PROFILE_JOB_STAGES = [
  { active: "Extracting", done: "Extracted" },
  { active: "Drafting", done: "Drafted" },
  { active: "Creating", done: "Created" },
] as const;
/** Mock % assumes profile generation takes ~2.5 minutes, then holds at 90% until review. */
const PROFILE_JOB_MOCK_DURATION_MS = 150_000;
const PROFILE_JOB_START_PCT = 5;
/** Cap while job is still running — review banner takes over when READY_FOR_REVIEW. */
const PROFILE_JOB_IN_PROGRESS_MAX_PCT = 90;

function serverProfileStageIndex(status: string): number {
  if (status === "CREATING") return 2;
  if (status === "DRAFTING") return 1;
  return 0;
}

function ProfileGenerationBanner({
  status,
  onCreateManually,
  creatingManually,
  sidebarOffsetPx,
  failed = false,
}: {
  status: string;
  onCreateManually: () => void;
  creatingManually: boolean;
  sidebarOffsetPx: number;
  failed?: boolean;
}) {
  const activeIndex = serverProfileStageIndex(status);
  const [progressPct, setProgressPct] = useState(PROFILE_JOB_START_PCT);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (failed) {
      setProgressPct(100);
      return undefined;
    }

    if (startedAtRef.current == null) {
      startedAtRef.current = Date.now();
    }
    const startedAt = startedAtRef.current;
    const tickMs = 250;
    const span = PROFILE_JOB_IN_PROGRESS_MAX_PCT - PROFILE_JOB_START_PCT;

    const tick = () => {
      const elapsed = Date.now() - startedAt;
      const t = Math.min(1, elapsed / PROFILE_JOB_MOCK_DURATION_MS);
      // Ease-out so early progress feels quicker, then settles into the long wait.
      const eased = 1 - (1 - t) * (1 - t);
      const next = PROFILE_JOB_START_PCT + span * eased;
      setProgressPct(Math.min(PROFILE_JOB_IN_PROGRESS_MAX_PCT, Math.round(next)));
    };

    tick();
    const timer = window.setInterval(tick, tickMs);
    return () => window.clearInterval(timer);
  }, [failed]);

  const displayPct = failed
    ? 100
    : Math.min(PROFILE_JOB_IN_PROGRESS_MAX_PCT, Math.round(progressPct));

  return (
    <Box
      pos="fixed"
      bottom={0}
      left={{ base: 0, sm: sidebarOffsetPx }}
      right={0}
      py={22}
      px={{ base: 16, sm: 28 }}
      style={{
        backgroundColor: "#04305c",
        zIndex: 100,
        borderTop: "1px solid rgba(255, 255, 255, 0.12)",
        boxShadow: "0 -10px 30px rgba(4, 48, 92, 0.35)",
      }}
    >
      <style>{`
        @keyframes gt-profile-step-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.45; }
        }
        .gt-profile-step-active { animation: gt-profile-step-pulse 1.35s ease-in-out infinite; }
      `}</style>
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Group gap="lg" wrap="nowrap" align="center" style={{ minWidth: 0 }}>
          {failed ? null : (
            <Text
              fw={700}
              c="#FFFFFF"
              style={{
                fontSize: 28,
                lineHeight: 1,
                letterSpacing: "-0.02em",
                fontVariantNumeric: "tabular-nums",
                minWidth: 64,
              }}
            >
              {displayPct}%
            </Text>
          )}
          <Box style={{ minWidth: 0 }}>
            <Text size="md" fw={600} c="#FFFFFF">
              {failed ? "We could not create your profile through your website" : "We are creating your profile"}
            </Text>
            <Text size="sm" c="rgba(255, 255, 255, 0.72)" mt={4}>
              {failed ? "Complete your profile to continue." : "Explore companies while you wait."}
            </Text>
          </Box>
        </Group>
        <Group gap="lg" wrap="wrap" align="center" justify="flex-end" style={{ marginLeft: "auto" }}>
          {failed ? null : (
          <Group gap="md" wrap="nowrap">
            {PROFILE_JOB_STAGES.map((stage, index) => {
              if (index < activeIndex) {
                return (
                  <Group key={stage.active} gap={6} wrap="nowrap">
                    <IconCircleCheck size={16} color="#34D399" stroke={2.2} />
                    <Text size="sm" fw={600} c="#FFFFFF">
                      {stage.done}
                    </Text>
                  </Group>
                );
              }
              if (index === activeIndex) {
                return (
                  <Group key={stage.active} gap={6} wrap="nowrap" className="gt-profile-step-active">
                    <Loader size={14} color="#FFFFFF" />
                    <Text size="sm" fw={700} c="#FFFFFF">
                      {stage.active}
                    </Text>
                  </Group>
                );
              }
              return (
                <Text key={stage.active} size="sm" fw={500} c="rgba(255, 255, 255, 0.45)">
                  {stage.active}
                </Text>
              );
            })}
          </Group>
          )}
          <Button
            variant="filled"
            size="md"
            loading={creatingManually}
            onClick={onCreateManually}
            styles={{
              root: {
                backgroundColor: "#FFFFFF",
                color: "#04305c",
                flexShrink: 0,
                "&:hover": { backgroundColor: "#F1F5F9" },
              },
            }}
          >
            {failed ? "Complete Profile" : "Create manually"}
          </Button>
        </Group>
      </Group>
    </Box>
  );
}

function ReviewGeneratedProfileBanner({
  onReview,
  sidebarOffsetPx,
}: {
  onReview: () => void;
  sidebarOffsetPx: number;
}) {
  return (
    <Box
      pos="fixed"
      bottom={0}
      left={{ base: 0, sm: sidebarOffsetPx }}
      right={0}
      py={16}
      px={{ base: 16, sm: 32 }}
      style={{
        backgroundColor: "#ECFDF5",
        zIndex: 100,
        borderTop: "2px solid #6EE7B7",
      }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Box style={{ minWidth: 0 }}>
          <Text size="md" fw={600} c="#065F46">
            Your profile is ready
          </Text>
          <Text size="sm" c="#047857">
            Review it and fill in any remaining details to access collaboration.
          </Text>
        </Box>
        <Button
          variant="filled"
          color="teal"
          size="sm"
          rightSection={<IconChevronRight size={16} />}
          onClick={onReview}
          style={{ flexShrink: 0 }}
        >
          Review profile
        </Button>
      </Group>
    </Box>
  );
}

type AppLayoutProps = {
  /** When set, renders instead of the matched child route `<Outlet />`. */
  children?: React.ReactNode;
};

export function AppLayout({ children }: AppLayoutProps = {}) {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile, open: openMobile }] = useDisclosure();
  const { desktopOpen: desktopOpened, toggleDesktop, closeDesktop } = usePersistedSidebarOpen(
    APP_SIDEBAR_STORAGE_KEY,
    true,
  );
  const sidebarLayout = useResponsiveSidebarLayout({
    variant: "company",
    desktopOpen: desktopOpened,
    closeDesktop,
  });
  const desktopSidebarOffsetPx = sidebarLayout.sidebarWidthPx;
  const [isProfileCreated, setIsProfileCreated] = useState(false);
  const {
    userDetails,
    setUserDetails,
    logout,
    shortlistedIds,
    shortlistedEcosystemHeadIds,
    shortlistedExpertIds,
    refetchUser,
    pendingRequestCounts,
    upcomingMeetingCount,
    collaborationBalance,
    collaborationBalanceLoadFailed,
  } = useUserContext();
  const flexDemo = useFlexDemoSessionOptional();
  const demoActive = Boolean(flexDemo?.demoActive);
  const { unreadCount, corridorUnread } = useNotifications();
  const clusterUnreadCount = corridorUnread.total;
  const exhibitorInquiryUnseenCount = useExhibitorInquiryUnseenCount(
    Boolean(userDetails?.hasActiveExpoMembership),
  );
  const {
    opened: trustpilotReportModalOpened,
    maybeOpen: maybeOpenReportTrustpilot,
    close: closeReportTrustpilot,
    onRate: onReportTrustpilotRate,
    remainingPlatforms: reportRemainingPlatforms,
  } = useTrustpilotRatingPrompt("collaboration_report_downloaded");
  const {
    opened: trustpilotMeetingModalOpened,
    maybeOpen: maybeOpenMeetingTrustpilot,
    close: closeMeetingTrustpilot,
    onRate: onMeetingTrustpilotRate,
    remainingPlatforms: meetingRemainingPlatforms,
  } = useTrustpilotRatingPrompt("first_meeting_booked");
  const trustpilotSyncedUserIdRef = useRef<string | null>(null);
  const shortlistCount = shortlistedIds.size + shortlistedEcosystemHeadIds.size + shortlistedExpertIds.size;
  const homeSidebarCount = pendingRequestCounts.received + upcomingMeetingCount;
  const homeSidebarAriaLabel = [
    pendingRequestCounts.received > 0
      ? `${pendingRequestCounts.received} new request${pendingRequestCounts.received === 1 ? "" : "s"}`
      : null,
    upcomingMeetingCount > 0
      ? `${upcomingMeetingCount} upcoming meeting${upcomingMeetingCount === 1 ? "" : "s"}`
      : null,
  ]
    .filter(Boolean)
    .join(", ");
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = userDetails?.company?.id;
  const companyStatus = userDetails?.company?.status;
  const profileJobStatus = (
    userDetails?.company as { websiteProfileJob?: { status?: string | null } | null } | null | undefined
  )?.websiteProfileJob?.status;
  const profileJobInProgress = isWebsiteProfileJobInProgress(
    userDetails?.company as { websiteProfileJob?: { status?: string | null } | null } | null | undefined,
  );
  const { count: myClusterCount, refresh: refreshMyClusterCount } = useMyEcosystemHeadCount(
    Boolean(companyId),
  );

  const [welcomeCreditsModalOpen, setWelcomeCreditsModalOpen] = useState(false);
  const [signupBonusCollaborations, setSignupBonusCollaborations] = useState(0);
  const [signupBonusExpiryMinutes, setSignupBonusExpiryMinutes] = useState(129600);
  const [verificationFeeCollaborations, setVerificationFeeCollaborations] = useState(0);
  const [verificationFeeCharged, setVerificationFeeCharged] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [companyIntentModalOpen, setCompanyIntentModalOpen] = useState(false);
  const [clusterJoinQuestionnaireLinkId, setClusterJoinQuestionnaireLinkId] = useState<string | null>(null);
  const [clusterJoinQuestionnaireOpen, setClusterJoinQuestionnaireOpen] = useState(false);
  const [intentCompleteModalOpen, setIntentCompleteModalOpen] = useState(false);
  const [intentStatusResolved, setIntentStatusResolved] = useState(false);
  const [intentQuestionnaireRequired, setIntentQuestionnaireRequired] = useState(false);
  const [onboardingPhase, setOnboardingPhase] = useState<OnboardingPhase>("idle");
  const lastAutoOpenPathRef = useRef<string | null>(null);
  const welcomeCreditsGateRef = useRef(false);
  /** Generated-profile Save and finish: after verification, skip credits and open 15Q. */
  const skipCreditsAfterVerificationRef = useRef(false);
  const beginQuestionnairePhaseRef = useRef<() => void>(() => {});
  const intentStatusCompanyIdRef = useRef<string | null>(null);
  const postOnboardingNavigatedRef = useRef(false);
  const onboardingPhaseRef = useRef<OnboardingPhase>("idle");
  onboardingPhaseRef.current = onboardingPhase;
  const clusterJoinSkippedRef = useRef(false);

  useEffect(() => {
    clusterJoinSkippedRef.current = false;
  }, [companyId]);

  const openVerificationModal = useCallback(() => setVerificationModalOpen(true), []);
  const openCompanyIntentModal = useCallback(() => {
    sessionStorage.removeItem(INTENT_QUESTIONNAIRE_SKIP_KEY);
    setCompanyIntentModalOpen(true);
  }, []);

  const inSequencedOnboarding =
    onboardingPhase === "credits" ||
    onboardingPhase === "verification" ||
    onboardingPhase === "tour" ||
    onboardingPhase === "questionnaire";

  // Sequenced flow: tour only during "tour" (after questionnaire Got it). Returning users: defer until intent gate settles.
  // Also defer while assigned flex demo is incomplete — Quick Tour runs once after demo completion.
  const deferSidebarTour =
    demoActive ||
    isFlexDemoAssigned(userDetails) ||
    clusterJoinQuestionnaireOpen ||
    (inSequencedOnboarding
      ? onboardingPhase !== "tour"
      : !intentStatusResolved ||
      companyIntentModalOpen ||
      intentCompleteModalOpen ||
      welcomeCreditsModalOpen ||
      verificationModalOpen ||
      sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY) === "1");

  const shouldForceVerification =
    Boolean(companyId) && shouldPromptCompanyRegistryVerification(userDetails);

  const isVerificationExemptRoute =
    location.pathname === "/create-profile" ||
    location.pathname === "/profile" ||
    location.pathname.startsWith("/profile/") ||
    location.pathname === "/settings" ||
    isCollaborationWalletPath(location.pathname) ||
    location.pathname === "/invoices" ||
    location.pathname === "/invoices/wallet" ||
    location.pathname === "/two-factor/setup";

  const openWelcomeCreditsStep = useCallback(() => {
    setOnboardingPhase("credits");
    void trpc.paymentConfig.get
      .query()
      .then((config) => {
        const bonus = config.signupBonusCollaborations ?? 0;
        setSignupBonusCollaborations(bonus);
        setSignupBonusExpiryMinutes(config.signupBonusExpiryMinutes ?? 129600);
        setVerificationFeeCollaborations(config.verificationFeeCollaborations ?? 0);
        if (bonus <= 0) {
          welcomeCreditsGateRef.current = false;
          setWelcomeCreditsModalOpen(false);
          beginQuestionnairePhaseRef.current();
          return;
        }
        setWelcomeCreditsModalOpen(true);
      })
      .catch(() => {
        setVerificationFeeCollaborations(0);
        welcomeCreditsGateRef.current = false;
        setWelcomeCreditsModalOpen(false);
        beginQuestionnairePhaseRef.current();
      });
  }, []);

  const advanceFromVerificationPhase = useCallback(() => {
    if (onboardingPhaseRef.current !== "verification") return;
    if (skipCreditsAfterVerificationRef.current) {
      skipCreditsAfterVerificationRef.current = false;
      welcomeCreditsGateRef.current = false;
      beginQuestionnairePhaseRef.current();
      return;
    }
    openWelcomeCreditsStep();
  }, [openWelcomeCreditsStep]);

  const {
    apply: handleApplyVerification,
    applying: verificationApplying,
    submitError: verificationSubmitError,
    setSubmitError: setVerificationSubmitError,
    verificationData,
    clearVerificationData,
  } = useApplyCompanyVerification({
    companyId,
    companyDetails: userDetails?.company
      ? {
        legalName: userDetails.company.legalName ?? "",
        headquartersCountry: userDetails.company.headquartersCountry ?? "",
        state: userDetails.company.state,
      }
      : null,
    onAfterSuccess: async () => {
      await refetchUser();
    },
    onApplyErrorClose: () => {
      setVerificationModalOpen(false);
      advanceFromVerificationPhase();
    },
    onPendingReview: () => {
      // Pending admin review does not charge the verification fee.
      // Hide Apply for verification immediately; refetch confirms UNDER_REVIEW.
      setUserDetails((prev) => {
        if (!prev?.company || prev.company.status === "UNDER_REVIEW") return prev;
        return { ...prev, company: { ...prev.company, status: "UNDER_REVIEW" } };
      });
      setVerificationModalOpen(false);
      advanceFromVerificationPhase();
    },
  });

  const isActive = (path: string, exact = false) => {
    if (demoActive && flexDemo) {
      if (path === "/my-discovery") return isFlexDemoExploreStep(flexDemo.session.step);
      if (path === "/collaborations") return isFlexDemoWorkspaceStep(flexDemo.session.step);
      return false;
    }
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const expoSignupLocked = isExpoSignupUser(userDetails);
  const expoVisitorHeader = expoSignupLocked ? getExpoVisitorRouteHeader(location.pathname) : {};
  const appHomePath = demoActive ? "/flex-demo" : expoSignupLocked ? EXPO_VISITOR_HOME_PATH : "/my-discovery";
  const appHomeAriaLabel = demoActive ? "Go to Explore" : expoSignupLocked ? "Go to Expos" : "Go to Explore";
  const navItems = expoSignupLocked
    ? [
        { label: "Expos", icon: IconCalendarEvent, path: EXPO_VISITOR_HOME_PATH },
        { label: "Shortlist", icon: IconBookmark, path: "/shortlist" },
        { label: "Inquiries", icon: IconMail, path: "/inquiries" },
        { label: "Updates", icon: IconBell, path: "/updates", exact: true },
      ]
    : [
    { label: "Explore", icon: IconSearch, path: "/my-discovery" },
    { label: "Home", icon: IconHome, path: COMPANY_HOME_PATH },
    { label: "My Profile", icon: IconLayoutGrid, path: "/profile", exact: true },
    { label: "My Clusters", icon: IconNetwork, path: MY_CLUSTER_PATH, exact: true },
    ...(userDetails?.hasActiveExpoMembership
      ? [{ label: "Inquiries", icon: IconMail, path: "/my-expos/inquiries" }]
      : []),
    { label: "Shortlist", icon: IconBookmark, path: "/shortlist" },
    { label: "Collaborations", icon: IconBriefcase, path: "/collaborations" },
    ...(userDetails?.role === "MANAGER"
      ? [
        { label: "Managed companies", icon: IconUsersGroup, path: "/management", exact: true },
        { label: "Partner suggestions", icon: IconSend, path: "/management/partner-suggestions", exact: true },
        { label: "Create company", icon: IconBuildingPlus, path: "/management/create-company", exact: true },
        { label: "Link company", icon: IconLink, path: "/management/add-company", exact: true },
      ]
      : []),
    { label: "Updates", icon: IconBell, path: "/updates", exact: true },
  ];

  const profileBrowseLocked = shouldDeferCompanyProfile(userDetails);
  const isProfileBrowseNavAllowed = (path: string) =>
    path === "/my-discovery" || path === COMPANY_HOME_PATH || path === MY_CLUSTER_PATH;

  const handleNavClick = (path: string) => {
    if (profileBrowseLocked && !isProfileBrowseNavAllowed(path)) return;
    if (!profileBrowseLocked && location.pathname === "/create-profile") return;
    if (demoActive && flexDemo) {
      if (path === "/collaborations" && isFlexDemoWorkspaceStep(flexDemo.session.step)) {
        flexDemo.persist({ ...flexDemo.session, collaborationsNavOpened: true, step: "workspace" });
        closeMobile();
        return;
      }
      if (path === "/my-discovery" && isFlexDemoExploreStep(flexDemo.session.step)) {
        closeMobile();
        return;
      }
      return;
    }
    if (location.pathname === path) {
      navigate(0);
      closeMobile();
      return;
    }
    if (location.pathname.startsWith(`${path}/`)) {
      navigate(path);
      closeMobile();
      return;
    }
    navigate(path);
    closeMobile();
  };

  const isDashboard = location.pathname === "/create-profile";

  // Flex Demo: open mobile drawer so Collaborations spotlight target is visible/clickable.
  const flexDemoStep = flexDemo?.session.step;
  const flexDemoCollabNavOpened = flexDemo?.session.collaborationsNavOpened;
  useEffect(() => {
    if (!demoActive || !flexDemoStep) return;
    if (flexDemoStep !== "workspace") return;
    if (flexDemoCollabNavOpened) return;
    if (typeof window === "undefined") return;
    const mobile = window.matchMedia("(max-width: 47.99em)"); // below Mantine `sm`
    if (!mobile.matches) return;
    openMobile();
  }, [demoActive, flexDemoStep, flexDemoCollabNavOpened, openMobile]);

  const sidebarCountBadgeStyles = (navActive: boolean): { root: CSSProperties; label: CSSProperties } => ({
    root: {
      backgroundColor: navActive ? "var(--mantine-color-primary-7)" : "#ffffff",
      color: navActive ? "#ffffff" : "var(--mantine-color-primary-7)",
      borderRadius: 9999,
      minWidth: 24,
      height: 24,
      padding: 0,
      paddingInline: 5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    label: {
      fontSize: 12,
      fontWeight: 800,
      lineHeight: 1,
    },
  });

  const handleLogout = () => {
    logout();
  };

  // Company status banner handling
  const isAdminBlocked = userDetails?.company?.adminBlocked || false;
  const blockedReason = userDetails?.company?.blockedReason;
  const showStatusBanner = isAdminBlocked || (companyStatus === "ACTION_REQUIRED" && location.pathname !== "/profile");
  const [creatingProfileManually, setCreatingProfileManually] = useState(false);
  const isProfileBuilderRoute =
    location.pathname === "/create-profile" ||
    location.pathname === "/profile/edit" ||
    location.pathname === "/review-account" ||
    location.pathname.startsWith("/review-account/");
  const showProfileGenerationBanner =
    profileJobInProgress && !isProfileBuilderRoute && !showStatusBanner;
  // Hide failed/complete banners on create, edit, and review — the form itself is the CTA.
  const showProfileGenerationFailedBanner =
    profileJobStatus === "FAILED" && !isProfileBuilderRoute && !showStatusBanner;
  const showReviewProfileBanner =
    profileJobStatus === "READY_FOR_REVIEW" &&
    !isProfileBuilderRoute &&
    !showStatusBanner &&
    !showProfileGenerationBanner;
  const showCompleteProfileBanner =
    profileBrowseLocked &&
    !profileJobInProgress &&
    profileJobStatus !== "READY_FOR_REVIEW" &&
    profileJobStatus !== "FAILED" &&
    !isProfileBuilderRoute &&
    !showStatusBanner;

  const profileJobStatusRef = useRef(profileJobStatus);
  profileJobStatusRef.current = profileJobStatus;
  const profileJobPathRef = useRef(location.pathname);
  profileJobPathRef.current = location.pathname;

  useEffect(() => {
    if (!profileJobInProgress) return undefined;
    let cancelled = false;
    let inFlight = false;

    const tick = async () => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const result = await trpc.company.websiteProfileJobStatus.query();
        if (cancelled) return;
        const next = result.status ?? null;
        const current = profileJobStatusRef.current ?? null;
        if (next === current) return;
        setUserDetails((prev) => {
          if (!prev?.company) return prev;
          return {
            ...prev,
            company: {
              ...prev.company,
              websiteProfileJob: next ? { status: next } : null,
            },
          };
        });
        if (!next || !WEBSITE_PROFILE_JOB_IN_PROGRESS_STATUSES.has(next)) {
          void refetchUser().then(() => {
            if (cancelled) return;
            // Background job finished while the user was still on the create form — send them to review.
            if (next === "READY_FOR_REVIEW" && profileJobPathRef.current === "/create-profile") {
              notifications.show({
                title: "Your profile is ready",
                message: "We finished generating it. Please review and save any changes.",
                color: "teal",
              });
              navigate("/profile/edit", { replace: true });
            }
          });
        }
      } catch {
        // Keep the current banner and try again on the next tick.
      } finally {
        inFlight = false;
      }
    };

    const timer = window.setInterval(() => {
      void tick();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [profileJobInProgress, navigate, refetchUser, setUserDetails]);

  const handleCreateProfileManually = () => {
    const companyId = userDetails?.company?.id;
    if (!companyId) {
      navigate("/create-profile");
      return;
    }
    setCreatingProfileManually(true);
    void trpc.company.cancelWebsiteProfileGeneration
      .mutate({ companyId })
      .then(() => refetchUser())
      .catch(() => {
        errorNotification({
          title: "Could not stop profile generation",
          message: "You can still fill the profile yourself.",
        });
      })
      .finally(() => {
        setCreatingProfileManually(false);
        navigate("/create-profile");
      });
  };

  const handleStatusBannerClick = () => {
    if (isAdminBlocked) {
      navigate("/settings"); // Navigate to settings/support for blocked accounts
    } else if (companyStatus === "ACTION_REQUIRED") {
      navigate("/profile"); // Navigate to edit profile for action required
    }
  };

  const navCtx = useMemo<AppLayoutNavContextValue>(
    () => ({ mobileOpened, toggleMobile, closeMobile }),
    [mobileOpened, toggleMobile, closeMobile]
  );

  const navigateToGuestPostOnboardingPath = useCallback(() => {
    if (postOnboardingNavigatedRef.current) return;
    const postPath = readGuestAuthPostOnboardingPath();
    if (!postPath) return;
    postOnboardingNavigatedRef.current = true;
    clearGuestAuthIntent();
    navigate(postPath, { replace: true });
  }, [navigate]);

  const maybeOpenClusterJoinQuestionnaire = useCallback(async () => {
    if (clusterJoinSkippedRef.current) return false;
    try {
      const res = await trpc.ecosystemHead.getMySignupJoinQuestionnaire.query();
      const linkId =
        res && typeof res === "object" && typeof (res as { linkId?: unknown }).linkId === "string"
          ? (res as { linkId: string }).linkId
          : null;
      if (linkId) {
        await trpc.ecosystemHead.getCompanyQuestionnaire.query({ linkId });
        setClusterJoinQuestionnaireLinkId(linkId);
        setClusterJoinQuestionnaireOpen(true);
        return true;
      }
    } catch {
      /* browse without join questionnaire */
    }
    setClusterJoinQuestionnaireLinkId(null);
    setClusterJoinQuestionnaireOpen(false);
    return false;
  }, []);

  /** After platform 15Q (or when it is skipped), continue cluster onboarding with head join Q. */
  const continueClusterOnboardingAfterIntent = useCallback(() => {
    void maybeOpenClusterJoinQuestionnaire().then((opened) => {
      if (onboardingPhaseRef.current !== "questionnaire") return;
      if (!opened) setOnboardingPhase("tour");
    });
  }, [maybeOpenClusterJoinQuestionnaire]);

  const beginQuestionnairePhase = useCallback(() => {
    clearIntentQuestionnaireSkip();
    setOnboardingPhase("questionnaire");

    if (isClusterSignupUser(userDetails)) {
      setIntentStatusResolved(true);
      setIntentQuestionnaireRequired(false);

      if (isClusterSignupPlatformIntentEnabled(userDetails)) {
        void trpc.companyIntent.status
          .query()
          .then((res: { required?: boolean } | null) => {
            if (onboardingPhaseRef.current !== "questionnaire") return;
            if (Boolean(res?.required)) {
              setCompanyIntentModalOpen(true);
              return;
            }
            setCompanyIntentModalOpen(false);
            continueClusterOnboardingAfterIntent();
          })
          .catch(() => {
            if (onboardingPhaseRef.current !== "questionnaire") return;
            setCompanyIntentModalOpen(false);
            continueClusterOnboardingAfterIntent();
          });
        return;
      }

      setCompanyIntentModalOpen(false);
      continueClusterOnboardingAfterIntent();
      return;
    }

    void trpc.companyIntent.status
      .query()
      .then((res: { required?: boolean } | null) => {
        if (onboardingPhaseRef.current !== "questionnaire") return;
        setIntentStatusResolved(true);
        const required = Boolean(res?.required);
        setIntentQuestionnaireRequired(false);
        if (required) {
          setCompanyIntentModalOpen(true);
          return;
        }
        setCompanyIntentModalOpen(false);
        if (isFlexDemoAssigned(userDetails) && isFlexDemoViewportSupported()) {
          navigate(FLEX_DEMO_PATH, { replace: true });
          return;
        }
        setOnboardingPhase("tour");
      })
      .catch(() => {
        if (onboardingPhaseRef.current !== "questionnaire") return;
        setIntentStatusResolved(true);
        setIntentQuestionnaireRequired(false);
        setCompanyIntentModalOpen(false);
        setOnboardingPhase("tour");
      });
  }, [continueClusterOnboardingAfterIntent, navigate, userDetails]);
  beginQuestionnairePhaseRef.current = beginQuestionnairePhase;

  const handleCloseClusterJoinQuestionnaire = useCallback(() => {
    clusterJoinSkippedRef.current = true;
    setClusterJoinQuestionnaireOpen(false);
    setClusterJoinQuestionnaireLinkId(null);
    if (onboardingPhaseRef.current === "questionnaire") {
      setOnboardingPhase("tour");
    }
    void refreshMyClusterCount();
  }, [refreshMyClusterCount]);

  const handleClusterJoinQuestionnaireUpdated = useCallback(() => {
    clusterJoinSkippedRef.current = true;
    setClusterJoinQuestionnaireOpen(false);
    setClusterJoinQuestionnaireLinkId(null);
    if (onboardingPhaseRef.current === "questionnaire") {
      setOnboardingPhase("tour");
    }
    notifyMyClusterCountChanged();
  }, []);

  const handleCloseCompanyIntentModal = useCallback(() => {
    markIntentQuestionnaireSkippedInSession();
    setCompanyIntentModalOpen(false);
    if (isClusterSignupUser(userDetails)) {
      continueClusterOnboardingAfterIntent();
      return;
    }
    if (onboardingPhaseRef.current === "questionnaire") {
      setOnboardingPhase("tour");
    }
  }, [continueClusterOnboardingAfterIntent, userDetails]);

  const handleCompanyIntentSubmitted = useCallback(() => {
    clearIntentQuestionnaireSkip();
    setCompanyIntentModalOpen(false);
    void refetchUser().finally(() => {
      setIntentCompleteModalOpen(true);
    });
  }, [refetchUser]);

  const handleIntentCompleteModalClose = useCallback(() => {
    setIntentCompleteModalOpen(false);
    if (
      isFlexDemoAssigned(userDetails) &&
      !isClusterSignupUser(userDetails) &&
      isFlexDemoViewportSupported()
    ) {
      setOnboardingPhase("done");
      navigate(FLEX_DEMO_PATH, { replace: true });
      return;
    }
    if (isClusterSignupUser(userDetails)) {
      if (onboardingPhaseRef.current === "idle") {
        setOnboardingPhase("questionnaire");
      }
      continueClusterOnboardingAfterIntent();
      return;
    }
    if (onboardingPhaseRef.current === "questionnaire" || onboardingPhaseRef.current === "idle") {
      setOnboardingPhase("tour");
    }
  }, [continueClusterOnboardingAfterIntent, navigate, userDetails]);

  /** Tour finished (or skipped) after the questionnaire success popup. */
  const handleTourComplete = useCallback(() => {
    if (onboardingPhaseRef.current !== "tour") return;
    setOnboardingPhase("done");
    navigateToGuestPostOnboardingPath();
  }, [navigateToGuestPostOnboardingPath]);

  const isMyDiscoveryRoute =
    location.pathname === "/my-discovery" || location.pathname.startsWith("/my-discovery/");
  const clusterSignupHomePath = getClusterSignupHomePath(userDetails);
  const isPostCreateOnboardingRoute =
    isMyDiscoveryRoute ||
    Boolean(clusterSignupHomePath && isClusterSignupHomePath(location.pathname, clusterSignupHomePath));

  // System verification success charges the fee; pending/skip do not.
  useEffect(() => {
    if (onboardingPhase === "verification" && verificationData) {
      setVerificationFeeCharged(true);
    }
  }, [onboardingPhase, verificationData]);

  // One-time post-create sequencer after first profile create → Explore or origin cluster.
  // Starts with verification, then credits (or 15Q when skip-credits is set). Defined before
  // the intent effect so the session key / gate is consumed first in this tree.
  useEffect(() => {
    if (!isPostCreateOnboardingRoute) return;
    const welcomeKey = sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY);
    if (welcomeKey !== "1") return;
    sessionStorage.removeItem(PROFILE_WELCOME_CREDITS_KEY);
    const skipCredits = sessionStorage.getItem(PROFILE_ONBOARDING_SKIP_CREDITS_KEY) === "1";
    if (skipCredits) {
      sessionStorage.removeItem(PROFILE_ONBOARDING_SKIP_CREDITS_KEY);
    }
    skipCreditsAfterVerificationRef.current = skipCredits;
    welcomeCreditsGateRef.current = true;
    setVerificationFeeCharged(false);
    setCompanyIntentModalOpen(false);
    if (shouldForceVerification && !isVerificationExemptRoute) {
      lastAutoOpenPathRef.current = location.pathname;
      if (companyId) {
        try {
          sessionStorage.setItem(verificationAutoOpenedSessionKey(companyId), "1");
        } catch {
          // ignore
        }
      }
      setOnboardingPhase("verification");
      setVerificationModalOpen(true);
      return;
    }
    skipCreditsAfterVerificationRef.current = false;
    if (skipCredits) {
      welcomeCreditsGateRef.current = false;
      beginQuestionnairePhaseRef.current();
      return;
    }
    openWelcomeCreditsStep();
  }, [isPostCreateOnboardingRoute, location.pathname, openWelcomeCreditsStep, shouldForceVerification, isVerificationExemptRoute, userDetails, companyId]);

  // Company intent questionnaire gate (blocks express-interest until completed).
  useEffect(() => {
    if (!companyId) {
      intentStatusCompanyIdRef.current = null;
      setIntentStatusResolved(false);
      setIntentQuestionnaireRequired(false);
      setCompanyIntentModalOpen(false);
      return;
    }

    // Never open questionnaire during earlier sequenced steps.
    // Sequenced questionnaire is opened explicitly after credits.
    if (
      onboardingPhase === "credits" ||
      onboardingPhase === "verification" ||
      onboardingPhase === "tour" ||
      onboardingPhase === "questionnaire" ||
      welcomeCreditsGateRef.current ||
      welcomeCreditsModalOpen ||
      verificationModalOpen ||
      sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY) === "1"
    ) {
      return;
    }

    // Avoid interrupting profile builder, GST and settings.
    if (
      location.pathname === "/create-profile" ||
      location.pathname.startsWith("/profile") ||
      location.pathname.startsWith("/settings")
    ) {
      if (intentStatusCompanyIdRef.current !== companyId) {
        intentStatusCompanyIdRef.current = companyId;
      }
      setIntentStatusResolved(true);
      setClusterJoinQuestionnaireOpen(false);
      return;
    }

    // After sequenced flow: do not auto-reopen.
    if (onboardingPhase === "done") {
      setIntentStatusResolved(true);
      return;
    }

    // Returning users (idle): 15Q opens only when reaching a company outside the cluster.
    const isInitialGateForCompany = intentStatusCompanyIdRef.current !== companyId;
    if (isInitialGateForCompany) {
      intentStatusCompanyIdRef.current = companyId;
    }

    if (isClusterSignupUser(userDetails)) {
      setIntentStatusResolved(true);
      setIntentQuestionnaireRequired(false);
      if (shouldForceVerification) {
        setCompanyIntentModalOpen(false);
        return;
      }
      if (isClusterSignupPlatformIntentEnabled(userDetails)) {
        void trpc.companyIntent.status
          .query()
          .then((res: { required?: boolean } | null) => {
            if (Boolean(res?.required)) {
              setCompanyIntentModalOpen(true);
              return;
            }
            setCompanyIntentModalOpen(false);
            void maybeOpenClusterJoinQuestionnaire();
          })
          .catch(() => {
            setCompanyIntentModalOpen(false);
            void maybeOpenClusterJoinQuestionnaire();
          });
        return;
      }
      setCompanyIntentModalOpen(false);
      void maybeOpenClusterJoinQuestionnaire();
      return;
    }

    setIntentStatusResolved(true);
    setIntentQuestionnaireRequired(false);
    setCompanyIntentModalOpen(false);
  }, [
    companyId,
    onboardingPhase,
    welcomeCreditsModalOpen,
    verificationModalOpen,
    location.pathname,
    maybeOpenClusterJoinQuestionnaire,
    userDetails,
    shouldForceVerification,
  ]);

  // Guest funnel: when questionnaire is not required, navigate after sequenced steps complete.
  useEffect(() => {
    // Questionnaire phase owns its own completion (open / skip-to-done / submit navigate).
    if (
      onboardingPhase === "credits" ||
      onboardingPhase === "verification" ||
      onboardingPhase === "tour" ||
      onboardingPhase === "questionnaire"
    ) {
      return;
    }
    if (!intentStatusResolved) return;
    if (welcomeCreditsModalOpen || welcomeCreditsGateRef.current) return;
    if (verificationModalOpen) return;
    if (companyIntentModalOpen) return;
    if (clusterJoinQuestionnaireOpen) return;
    if (intentCompleteModalOpen) return;
    if (intentQuestionnaireRequired) return;
    navigateToGuestPostOnboardingPath();
  }, [
    onboardingPhase,
    intentStatusResolved,
    welcomeCreditsModalOpen,
    verificationModalOpen,
    companyIntentModalOpen,
    clusterJoinQuestionnaireOpen,
    intentCompleteModalOpen,
    intentQuestionnaireRequired,
    navigateToGuestPostOnboardingPath,
  ]);

  const handleWelcomeCreditsClose = useCallback(() => {
    welcomeCreditsGateRef.current = false;
    setWelcomeCreditsModalOpen(false);
    beginQuestionnairePhase();
  }, [beginQuestionnairePhase]);

  // Auto-open verification once per company session (not on every route change).
  useEffect(() => {
    if (onboardingPhase !== "idle" && onboardingPhase !== "done") return;
    const welcomeKey = sessionStorage.getItem(PROFILE_WELCOME_CREDITS_KEY);
    if (!shouldForceVerification || !companyId) return;
    if (isVerificationExemptRoute) return;
    if (welcomeKey === "1") return;
    if (welcomeCreditsGateRef.current || welcomeCreditsModalOpen) return;
    if (verificationModalOpen) return;
    const autoOpenKey = verificationAutoOpenedSessionKey(companyId);
    try {
      if (sessionStorage.getItem(autoOpenKey) === "1") return;
      sessionStorage.setItem(autoOpenKey, "1");
    } catch {
      // Fall through and open once this mount if sessionStorage is unavailable.
      if (lastAutoOpenPathRef.current === "session") return;
      lastAutoOpenPathRef.current = "session";
    }
    setVerificationModalOpen(true);
  }, [
    onboardingPhase,
    shouldForceVerification,
    isVerificationExemptRoute,
    companyId,
    welcomeCreditsModalOpen,
    verificationModalOpen,
  ]);

  // Sync Trustpilot completion flags from DB → localStorage (and migrate local-only marks up).
  useEffect(() => {
    const userId = userDetails?.id;
    if (!userId) {
      trustpilotSyncedUserIdRef.current = null;
      return;
    }
    if (trustpilotSyncedUserIdRef.current === userId) return;
    trustpilotSyncedUserIdRef.current = userId;
    void syncTrustpilotPromptsFromServer(userId);
  }, [userDetails?.id]);

  const tryOpenPendingTrustpilotPrompt = useCallback((
    milestone: "collaboration_report_downloaded" | "first_meeting_booked",
    openPrompt: (userId: string) => void,
  ) => {
    if (onboardingPhase !== "idle" && onboardingPhase !== "done") return;
    if (welcomeCreditsModalOpen || verificationModalOpen || companyIntentModalOpen || intentCompleteModalOpen) return;
    if (!userDetails?.id) return;
    if (trustpilotReportModalOpened || trustpilotMeetingModalOpened) return;

    const pending = readPendingTrustpilotPrompt(userDetails.id);
    if (pending !== milestone) return;

    // Keep pending until Rate / Not now so a mid-prompt reload can reopen the modal.
    openPrompt(userDetails.id);
  }, [
    onboardingPhase,
    welcomeCreditsModalOpen,
    verificationModalOpen,
    companyIntentModalOpen,
    intentCompleteModalOpen,
    userDetails?.id,
    trustpilotReportModalOpened,
    trustpilotMeetingModalOpened,
  ]);

  useEffect(() => {
    tryOpenPendingTrustpilotPrompt("collaboration_report_downloaded", maybeOpenReportTrustpilot);
    tryOpenPendingTrustpilotPrompt("first_meeting_booked", maybeOpenMeetingTrustpilot);
  }, [tryOpenPendingTrustpilotPrompt, maybeOpenReportTrustpilot, maybeOpenMeetingTrustpilot, location.pathname]);

  useEffect(() => {
    const onQueued = (event: Event) => {
      const detail = (event as CustomEvent<{ userId?: string; milestone?: string }>).detail;
      if (!detail?.userId || detail.userId !== userDetails?.id) return;
      if (
        detail.milestone !== "collaboration_report_downloaded" &&
        detail.milestone !== "first_meeting_booked"
      ) {
        return;
      }
      // Defer so any in-flight navigation can settle before opening.
      window.setTimeout(() => {
        if (detail.milestone === "collaboration_report_downloaded") {
          tryOpenPendingTrustpilotPrompt("collaboration_report_downloaded", maybeOpenReportTrustpilot);
          return;
        }
        tryOpenPendingTrustpilotPrompt("first_meeting_booked", maybeOpenMeetingTrustpilot);
      }, 0);
    };
    window.addEventListener(TRUSTPILOT_PROMPT_QUEUED_EVENT, onQueued);
    return () => window.removeEventListener(TRUSTPILOT_PROMPT_QUEUED_EVENT, onQueued);
  }, [
    tryOpenPendingTrustpilotPrompt,
    maybeOpenReportTrustpilot,
    maybeOpenMeetingTrustpilot,
    userDetails?.id,
  ]);

  const handleCloseVerificationModal = () => {
    setVerificationModalOpen(false);
    clearVerificationData();
    advanceFromVerificationPhase();
  };

  const verificationCtx = useMemo<VerificationModalContextValue>(
    () => ({ openVerificationModal }),
    [openVerificationModal],
  );

  const companyIntentCtx = useMemo<CompanyIntentModalContextValue>(
    () => ({ openCompanyIntentModal }),
    [openCompanyIntentModal],
  );

  return (
    <ProfileContext.Provider value={{ isProfileCreated, setIsProfileCreated }}>
      <AppLayoutNavContext.Provider value={navCtx}>
        <CompanyIntentModalContext.Provider value={companyIntentCtx}>
          <VerificationModalContext.Provider value={verificationCtx}>
            <AppShellSidebarProvider
              desktopSidebarExpanded={sidebarLayout.sidebarExpanded}
              sidebarMode={sidebarLayout.mode}
              layoutDensity={sidebarLayout.layoutDensity}
              sidebarWidthPx={sidebarLayout.sidebarWidthPx}
            >
              <AppShell
                layout="alt"
                header={{
                  height: expoSignupLocked
                    ? { base: 64, sm: 64 }
                    : { base: 60 + AUTH_MOBILE_EXPLORE_ROW_HEIGHT, sm: 64 },
                }}
                navbar={{
                  width: sidebarLayout.sidebarWidthPx,
                  breakpoint: "sm",
                  collapsed: { mobile: !mobileOpened, desktop: false },
                }}
                padding="0"
                style={{
                  minHeight: "100dvh",
                  ["--gt-layout-density" as string]: sidebarLayout.layoutDensity,
                }}
              >
                <AppShell.Header style={{ zIndex: 1000, borderBottom: "1px solid #E5E7EB", backgroundColor: "#fff" }}>
                  <Group
                    h={expoSignupLocked ? "100%" : 60}
                    px="md"
                    justify="space-between"
                    wrap="nowrap"
                    hiddenFrom="sm"
                    gap="sm"
                  >
                    <SidebarLogoLink
                      to={appHomePath}
                      height={40}
                      ariaLabel={appHomeAriaLabel}
                      onNavigate={closeMobile}
                      style={{ flexShrink: 0 }}
                    />
                    {expoVisitorHeader.title ? (
                      <ExpoVisitorHeaderCopy
                        title={expoVisitorHeader.title}
                        description={expoVisitorHeader.description}
                      />
                    ) : null}
                    <Burger
                      opened={mobileOpened}
                      onClick={toggleMobile}
                      size="sm"
                      color="var(--mantine-color-primary-7)"
                      style={{ flexShrink: 0 }}
                    />
                  </Group>
                  {expoSignupLocked ? null : (
                    <Box hiddenFrom="sm">
                      <AuthExploreMobileTabsRow />
                    </Box>
                  )}
                  <Box visibleFrom="sm" h="100%" w="100%">
                    <AuthExploreTopHeader />
                  </Box>
                </AppShell.Header>

                <AppShell.Navbar
                  p={sidebarLayout.showNavLabels ? sidebarLayout.navPaddingX : "xs"}
                  className="app-navbar-mobile-fit"
                  style={{
                    backgroundColor: "var(--mantine-color-primary-6)",
                    backgroundImage:
                      "repeating-linear-gradient(0deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(90deg, rgba(255,255,255,0.06) 0, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 40px)",
                    backgroundSize: "auto",
                    backgroundPosition: "top left",
                    borderRight: "1px solid rgba(255, 255, 255, 0.14)",
                  }}
                >
                  {/* HEADER / BRANDING */}
                  <AppShell.Section mb="md" visibleFrom="sm">
                    {sidebarLayout.sidebarExpanded ? (
                      <Group justify="space-between" align="center" wrap="nowrap">
                        <Flex align="center" gap="xs" style={{ minWidth: 0, flex: 1, justifyContent: "center" }}>
                          <SidebarLogoLink
                            to={appHomePath}
                            height={60}
                            ariaLabel={appHomeAriaLabel}
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                          {demoActive ? (
                            <Badge color="yellow" variant="filled" c="primary.7" size="sm">
                              Demo
                            </Badge>
                          ) : null}
                        </Flex>
                        <SidebarRailToggleButton expanded onClick={toggleDesktop} color="white" />
                      </Group>
                    ) : (
                      <Flex justify="center" mb="xs">
                        <SidebarRailToggleButton expanded={false} onClick={toggleDesktop} color="white" />
                      </Flex>
                    )}

                    {sidebarLayout.sidebarExpanded ? <Divider mt="sm" color="rgba(255, 255, 255, 0.9)" /> : null}
                  </AppShell.Section>

                  {/* NAV LINKS */}
                  <AppShell.Section grow component={ScrollArea}>
                    <div style={{ display: "flex", flexDirection: "column", gap: sidebarLayout.showNavLabels ? 8 : 4 }}>
                      {navItems.map((item) => {
                        const demoLocked =
                          demoActive && flexDemo
                            ? !isFlexDemoNavAllowed(item.path, flexDemo.session.step)
                            : false;
                        const disabled =
                          demoLocked ||
                          (profileBrowseLocked
                            ? !isProfileBrowseNavAllowed(item.path)
                            : isDashboard);
                        const active = isActive(item.path, "exact" in item ? item.exact : false);
                        const sidebarTourId = item.path.replace(/^\//, "");
                        const flexDemoTourId =
                          item.path === "/collaborations"
                            ? "nav-collaborations"
                            : item.path === "/my-discovery"
                              ? "nav-explore"
                              : undefined;
                        const disabledTooltip = demoLocked
                          ? flexDemoNavLockedTooltip(item.path)
                          : "Complete your profile to unlock this feature";

                        if (!sidebarLayout.showNavLabels) {
                          const badgeCount =
                            item.path === "/shortlist" && shortlistCount > 0
                              ? shortlistCount
                              : item.path === MY_CLUSTER_PATH && (clusterUnreadCount > 0 || myClusterCount > 0)
                                ? clusterUnreadCount > 0
                                  ? clusterUnreadCount
                                  : myClusterCount
                                : item.path === COMPANY_HOME_PATH && homeSidebarCount > 0
                                  ? homeSidebarCount
                                  : item.path === "/updates" && unreadCount > 0
                                    ? unreadCount
                                    : item.path === EXHIBITOR_INQUIRIES_PATH && exhibitorInquiryUnseenCount > 0
                                      ? exhibitorInquiryUnseenCount
                                      : undefined;

                          return (
                            <Box key={item.label} data-sidebar-tour={sidebarTourId} data-flex-demo-tour={flexDemoTourId}>
                              <Tooltip
                                label={disabled ? disabledTooltip : item.label}
                                disabled={!disabled}
                                position="right"
                                withArrow
                              >
                                <Box>
                                  <SidebarIconRailLink
                                    icon={item.icon}
                                    label={item.label}
                                    active={active}
                                    disabled={disabled}
                                    onClick={() => handleNavClick(item.path)}
                                    badgeCount={badgeCount}
                                  />
                                </Box>
                              </Tooltip>
                            </Box>
                          );
                        }

                        return (
                          <Box key={item.label} data-sidebar-tour={sidebarTourId} data-flex-demo-tour={flexDemoTourId}>
                            <Tooltip
                              label={disabledTooltip}
                              disabled={!disabled}
                              position="right"
                              withArrow
                            >
                              <NavLink
                                label={
                                  <Group justify="space-between" style={{ width: "100%" }}>
                                    <Text
                                      size={sidebarLayout.mode === "compact-expanded" ? "xs" : "sm"}
                                      fw={600}
                                      className="sidebar-nav-text"
                                      c={active ? "primary.7" : "white"}
                                    >
                                      {item.label}
                                    </Text>

                                    {item.path === "/shortlist" && shortlistCount > 0 && (
                                      <Badge size="xs" styles={sidebarCountBadgeStyles(active)}>
                                        {shortlistCount}
                                      </Badge>
                                    )}
                                    {item.path === MY_CLUSTER_PATH && clusterUnreadCount > 0 ? (
                                      <TabCountBadge
                                        count={clusterUnreadCount}
                                        label={`${clusterUnreadCount} new cluster update${clusterUnreadCount === 1 ? "" : "s"}`}
                                        style={{ boxShadow: "none" }}
                                      />
                                    ) : (
                                      item.path === MY_CLUSTER_PATH &&
                                      myClusterCount > 0 && (
                                        <Badge
                                          size="xs"
                                          styles={sidebarCountBadgeStyles(active)}
                                          aria-label={`${myClusterCount} cluster${myClusterCount === 1 ? "" : "s"} joined`}
                                        >
                                          {myClusterCount}
                                        </Badge>
                                      )
                                    )}
                                    {item.path === COMPANY_HOME_PATH && homeSidebarCount > 0 && (
                                      <Badge
                                        size="xs"
                                        styles={sidebarCountBadgeStyles(active)}
                                        aria-label={homeSidebarAriaLabel}
                                      >
                                        {homeSidebarCount}
                                      </Badge>
                                    )}
                                    {item.path === "/updates" && unreadCount > 0 && (
                                      <Badge
                                        size="xs"
                                        styles={sidebarCountBadgeStyles(active)}
                                        aria-label={`${unreadCount} unread update${unreadCount === 1 ? "" : "s"}`}
                                      >
                                        {unreadCount}
                                      </Badge>
                                    )}
                                    {item.path === EXHIBITOR_INQUIRIES_PATH && exhibitorInquiryUnseenCount > 0 && (
                                      <Badge
                                        size="xs"
                                        styles={sidebarCountBadgeStyles(active)}
                                        aria-label={`${exhibitorInquiryUnseenCount} new inquir${exhibitorInquiryUnseenCount === 1 ? "y" : "ies"}`}
                                      >
                                        {exhibitorInquiryUnseenCount > 99 ? "99+" : exhibitorInquiryUnseenCount}
                                      </Badge>
                                    )}
                                  </Group>
                                }
                                leftSection={
                                  <item.icon
                                    size="1.2rem"
                                    stroke={1.8}
                                    color={active ? "var(--mantine-color-primary-7)" : "white"}
                                  />
                                }
                                active={active}
                                onClick={() => handleNavClick(item.path)}
                                disabled={disabled}
                                variant="subtle"
                                styles={{
                                  root: {
                                    borderRadius: 8,
                                    padding:
                                      sidebarLayout.mode === "compact-expanded" ? "8px 10px" : "10px 12px",
                                    opacity: disabled ? 0.5 : 1,
                                    cursor: disabled ? "not-allowed" : "pointer",
                                    backgroundColor: active ? "#ffc300" : "transparent",
                                    color: active ? "var(--mantine-color-primary-7)" : "white",
                                    "&:hover": {
                                      backgroundColor: "#ffc300",
                                      color: "var(--mantine-color-primary-7)",
                                      "& svg": {
                                        color: "var(--mantine-color-primary-7)",
                                      },
                                      "& .sidebar-nav-text": {
                                        color: "var(--mantine-color-primary-7)",
                                      },
                                    },
                                  },
                                  label: {
                                    color: "inherit",
                                  },
                                }}
                              />
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </div>
                  </AppShell.Section>

                  {/* SIDEBAR FOOTER */}
                  <AppShell.Section>
                    <Box visibleFrom="sm">
                      <SidebarDesktopFooter
                        creditAvailable={collaborationBalance}
                        creditLoadFailed={collaborationBalanceLoadFailed}
                        companyName={userDetails?.company?.legalName || userDetails?.name || "Guest"}
                        gtid={userDetails?.company?.gtid || userDetails?.email || "User"}
                        companyStatus={companyStatus}
                        onLogout={handleLogout}
                        collapsed={!sidebarLayout.sidebarExpanded}
                        showCredits={!expoSignupLocked}
                        hideProfileActions={expoSignupLocked}
                      />
                    </Box>
                    <Box hiddenFrom="sm">
                      <Stack gap="sm">
                        {expoSignupLocked ? null : (
                        <UnstyledButton
                          w="100%"
                          px="xs"
                          py={4}
                          onClick={() => {
                            if (demoActive) return;
                            navigate(COLLABORATION_WALLET_PATH);
                          }}
                          style={{
                            borderRadius: 8,
                            textAlign: "left",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <Text c="white" size="xs" style={{ opacity: 0.85 }}>
                            Available collaboration opportunities
                          </Text>
                          {collaborationBalanceLoadFailed ? (
                            <Text size="sm" fw={600} c="white">
                              —
                            </Text>
                          ) : collaborationBalance === null ? (
                            <Loader size="xs" color="white" type="dots" />
                          ) : (
                            <Text size="lg" fw={600} c="white" lh={1.2}>
                              {collaborationBalance}
                            </Text>
                          )}
                        </UnstyledButton>
                        )}
                        <Menu shadow="md" width="target" position="top">
                          <Menu.Target>
                            <UnstyledButton
                              w="100%"
                              p="xs"
                              style={{
                                borderRadius: 8,
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.12)")}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                            >
                              <Group>
                                <Avatar
                                  radius="xl"
                                  style={{
                                    backgroundColor: "white",
                                    border: "1px solid rgba(255, 255, 255, 0.18)",
                                  }}
                                >
                                  <IconSettings size={18} color="var(--mantine-color-primary-7)" />
                                </Avatar>
                                <div style={{ flex: 1, overflow: "hidden" }}>
                                  <Text size="sm" fw={600} c="white" truncate>
                                    {userDetails?.company?.legalName || "Company Name"}
                                  </Text>
                                  <Text c="white" size="xs" truncate style={{ opacity: 0.85 }}>
                                    {userDetails?.company?.gtid || "User"}
                                  </Text>
                                </div>
                              </Group>
                            </UnstyledButton>
                          </Menu.Target>
                          <Menu.Dropdown
                            style={{
                              backgroundColor: "var(--mantine-color-primary-6)",
                              border: "1px solid rgba(255, 255, 255, 0.14)",
                            }}
                          >
                            {expoSignupLocked ? null : (
                            <>
                            <Menu.Item
                              c="white"
                              fw={600}
                              leftSection={<IconUser size={16} />}
                              onClick={() => navigate("/account")}
                              style={{ borderRadius: 8 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffc300";
                                e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "white";
                              }}
                            >
                              My Profile
                            </Menu.Item>
                            <Menu.Item
                              c="white"
                              fw={600}
                              leftSection={<IconPencil size={16} />}
                              onClick={() => {
                                if (companyStatus === "UNDER_REVIEW") {
                                  sessionStorage.setItem(
                                    PROFILE_VERIFICATION_LANDING_KEY,
                                    JSON.stringify({ kind: "EDIT_LOCKED_UNDER_REVIEW" })
                                  );
                                  navigate("/profile", {
                                    replace: true,
                                    state: { lockNoticeAt: Date.now() },
                                  });
                                  return;
                                }
                                navigate("/profile/edit");
                              }}
                              style={{ borderRadius: 8 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffc300";
                                e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "white";
                              }}
                            >
                              Edit Profile
                            </Menu.Item>
                            <Menu.Item
                              c="white"
                              fw={600}
                              leftSection={<IconCoins size={16} />}
                              onClick={() => {
                                if (demoActive) return;
                                navigate(COLLABORATION_WALLET_PATH);
                              }}
                              style={{ borderRadius: 8 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffc300";
                                e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "white";
                              }}
                            >
                              Collaboration wallet
                            </Menu.Item>
                            </>
                            )}
                            <Menu.Item
                              c="white"
                              fw={600}
                              leftSection={<IconHelpCircle size={16} />}
                              onClick={() => navigate("/settings")}
                              style={{ borderRadius: 8 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffc300";
                                e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "white";
                              }}
                            >
                              Settings
                            </Menu.Item>
                            {expoSignupLocked ? (
                            <Menu.Item
                              c="white"
                              fw={600}
                              leftSection={<IconUser size={16} />}
                              onClick={() => navigate("/account")}
                              style={{ borderRadius: 8 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffc300";
                                e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "white";
                              }}
                            >
                              My Profile
                            </Menu.Item>
                            ) : null}
                            <Menu.Item
                              c="white"
                              fw={600}
                              leftSection={<IconLogout size={16} />}
                              onClick={handleLogout}
                              style={{ borderRadius: 8 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#ffc300";
                                e.currentTarget.style.color = "var(--mantine-color-primary-7)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                                e.currentTarget.style.color = "white";
                              }}
                            >
                              Logout
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Stack>
                    </Box>
                  </AppShell.Section>
                </AppShell.Navbar>

                {/* overflowX: clip (not hidden) — hidden forces overflow-y:auto and a second scrollbar on tall pages like My Profile */}
                <PortalAppShellMain>
                  <PortalMainShell
                    style={{
                      paddingBottom:
                        showStatusBanner ||
                        showCompleteProfileBanner ||
                        showProfileGenerationBanner ||
                        showProfileGenerationFailedBanner ||
                        showReviewProfileBanner
                          ? 140
                          : 0,
                    }}
                  >
                    <Suspense fallback={<AppShellRouteFallback />}>
                      {children ?? <Outlet />}
                    </Suspense>
                  </PortalMainShell>
                  <CompanyStatusBanner
                    adminBlocked={isAdminBlocked}
                    blockedReason={blockedReason}
                    companyStatus={location.pathname.startsWith("/profile") ? undefined : companyStatus}
                    onViewMore={handleStatusBannerClick}
                    sidebarOffsetPx={desktopSidebarOffsetPx}
                  />
                  {showProfileGenerationBanner && profileJobStatus ? (
                    <ProfileGenerationBanner
                      status={profileJobStatus}
                      creatingManually={creatingProfileManually}
                      sidebarOffsetPx={desktopSidebarOffsetPx}
                      onCreateManually={handleCreateProfileManually}
                    />
                  ) : null}
                  {showProfileGenerationFailedBanner ? (
                    <ProfileGenerationBanner
                      status="FAILED"
                      failed
                      creatingManually={false}
                      sidebarOffsetPx={desktopSidebarOffsetPx}
                      onCreateManually={() => {
                        if (location.pathname === "/create-profile") {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          return;
                        }
                        navigate("/create-profile");
                      }}
                    />
                  ) : null}
                  {showReviewProfileBanner ? (
                    <ReviewGeneratedProfileBanner
                      sidebarOffsetPx={desktopSidebarOffsetPx}
                      onReview={() => navigate("/profile/edit")}
                    />
                  ) : null}
                  {showCompleteProfileBanner ? (
                    <CompleteProfileBanner
                      sidebarOffsetPx={desktopSidebarOffsetPx}
                      onComplete={() => navigate("/create-profile")}
                    />
                  ) : null}
                  <CompleteProfilePrompt />
                  <SidebarOnboardingTour defer={deferSidebarTour} onComplete={handleTourComplete} />
                  <NewRequestsSpotlight />
                  <ProfileWelcomeCreditsModal
                    opened={welcomeCreditsModalOpen}
                    onClose={handleWelcomeCreditsClose}
                    onContinue={handleWelcomeCreditsClose}
                    signupBonusCollaborations={signupBonusCollaborations}
                    signupBonusExpiryMinutes={signupBonusExpiryMinutes}
                    verificationFeeCollaborations={verificationFeeCollaborations}
                    verificationFeeCharged={verificationFeeCharged}
                  />
                  <ApplyVerificationModal
                    opened={verificationModalOpen}
                    onClose={handleCloseVerificationModal}
                    onApply={handleApplyVerification}
                    isLoading={verificationApplying}
                    verificationData={verificationData}
                    headquartersCountry={userDetails?.company?.headquartersCountry || ""}
                    initialRegistrationIdType={userDetails?.company?.registrationIdType || null}
                    initialRegistrationNumber={userDetails?.company?.registrationNumber || ""}
                    optionsLoading={userDetails === undefined}
                    submitError={verificationSubmitError}
                    onClearSubmitError={() => setVerificationSubmitError(null)}
                    dismissible={false}
                  />
                  <CompanyIntentQuestionnaireModal
                    opened={companyIntentModalOpen}
                    onClose={handleCloseCompanyIntentModal}
                    onSubmitted={handleCompanyIntentSubmitted}
                  />
                  <EcosystemHeadCompanyQuestionnaireModal
                    linkId={clusterJoinQuestionnaireLinkId}
                    opened={clusterJoinQuestionnaireOpen}
                    onClose={handleCloseClusterJoinQuestionnaire}
                    onUpdated={handleClusterJoinQuestionnaireUpdated}
                  />
                  <IntentQuestionnaireCompleteModal
                    opened={intentCompleteModalOpen}
                    onClose={handleIntentCompleteModalClose}
                  />
                  <TrustpilotRatingModal
                    opened={trustpilotReportModalOpened}
                    onClose={closeReportTrustpilot}
                    onRate={onReportTrustpilotRate}
                    title="Report downloaded successfully"
                    description="You have downloaded your collaboration report. If GTsetu helped you get here, please rate us on Trustpilot."
                    visiblePlatforms={reportRemainingPlatforms}
                  />
                  <TrustpilotRatingModal
                    opened={trustpilotMeetingModalOpened}
                    onClose={closeMeetingTrustpilot}
                    onRate={onMeetingTrustpilotRate}
                    title="Meeting booked successfully"
                    description="Your first meeting has been booked successfully. Please rate us on Trustpilot."
                    visiblePlatforms={meetingRemainingPlatforms}
                  />
                </PortalAppShellMain>
              </AppShell>
            </AppShellSidebarProvider>
          </VerificationModalContext.Provider>
        </CompanyIntentModalContext.Provider>
      </AppLayoutNavContext.Provider>
    </ProfileContext.Provider>
  );
}

export default AppLayout;
